import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { DataSourceOrdenacaoInsensivel } from '../../../../shared/helpers/ordenacao-tabela.helper';
import { RouterModule } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '../../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { UtilService } from '../../../../shared/services/util.service';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LinhaProdutoService } from '../../../../shared/services/apis/produto/linha-produto.service';
import { LinhaProdutoStruct } from '../../../../shared/services/structs/produto/linha-produto.struct';
import { LoadingBoxDefaultListComponent } from '../../../../shared/components/loading-box-default-list/loading-box-default-list.component';

@Component({
  selector: 'app-config-linha-produtos',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatCheckboxModule,
    MatMenuModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    EmptyListComponent,
    RouterModule,
    HeaderComponent,
    LoadingBoxDefaultListComponent,
  ],
  templateUrl: './config-linha-produtos.component.html',
  styleUrl: './config-linha-produtos.component.css',
})
export class ConfigLinhaProdutosComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Cadastros;

  public model: FormGroup;
  public _paginaAtual: number = 1;
  public _tamanhoPagina: number = 20;
  public _totalRegistros: number = 0;

  public _dataSource = new DataSourceOrdenacaoInsensivel<LinhaProdutoStruct>();
  public _isLoading: boolean = false;

  public displayedColumns: string[] = ['select', 'descricao', 'tipoComissao', 'actions'];

  public selection = new SelectionModel<LinhaProdutoStruct>(true, []);

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this._dataSource.sort = sort;
  }

  constructor(
    private formBuilder: FormBuilder,
    private linhaProdutoService: LinhaProdutoService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private utilService: UtilService
  ) {}

  ngOnInit(): void {
    this.model = this.formBuilder.group({
      pesquisa: [''],
    });

    this.loadFilters();
    this.carregarLinhasProduto();
  }

  ngOnDestroy(): void {
    this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, [
      '/configuracoes/cadastros/linha-produtos/cadastrar',
    ]);
  }

  private loadFilters(): void {
    const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (filtroSalvo) {
      this.model.patchValue(filtroSalvo);
    }
  }

  public carregarLinhasProduto(reset: boolean = false): void {
    this._isLoading = true;
    const descricao = this.model.get('pesquisa')?.value || '';

    this.linhaProdutoService.getAll(descricao, this._paginaAtual, this._tamanhoPagina).subscribe({
      next: response => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show(
            'Atenção',
            response.errorDescription || 'Erro ao carregar linhas de produto',
            AlertType.warning
          );
          return;
        }

        this._totalRegistros = response.totalRegistros ?? 0;

        const newItems = response.listaLinhaProduto ?? [];

        if (reset) {
          this._dataSource.data = newItems;
        } else {
          this._dataSource.data = [...this._dataSource.data, ...newItems];
        }
      },
      error: err => {
        this._isLoading = false;
        this.alertService.show(
          'Erro inesperado',
          err.message || 'Erro ao carregar linhas de produto',
          AlertType.error
        );
      },
    });
  }

  public proximaPagina(): void {
    this._paginaAtual++;
    this.carregarLinhasProduto();
  }

  public clickPesquisar(): void {
    this._paginaAtual = 1;
    this._dataSource.data = [];
    this.selection.clear();
    this.carregarLinhasProduto(true);
  }

  public isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this._dataSource.data.length;
    return numSelected === numRows && numRows > 0;
  }

  public toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }
    this.selection.select(...this._dataSource.data);
  }

  public abrirModalExcluir(item?: LinhaProdutoStruct): void {
    const isBulk = !item;
    const itemsToDelete = isBulk ? this.selection.selected : [item!];

    if (itemsToDelete.length === 0) return;

    const hasLinkedProducts = itemsToDelete.some(linha => linha.possuiProdutos === true);
    const description = hasLinkedProducts
      ? 'Existem produtos vinculados a algumas das linhas selecionadas. Confirmar a exclusão das linhas selecionadas?'
      : 'Confirma a exclusão das linhas selecionadas?';

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmar exclusão',
        description: description,
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirm) {
        this._isLoading = true;

        if (isBulk) {
          const ids = itemsToDelete.map(i => i.idLinhaProduto);
          this.linhaProdutoService.deleteBulk(ids).subscribe({
            next: response => {
              this._isLoading = false;
              if (response.isError) {
                this.alertService.show(
                  'Atenção',
                  response.errorDescription || 'Erro ao excluir itens',
                  AlertType.warning
                );
              } else {
                this.alertService.show(
                  'Sucesso',
                  'Linhas de produto excluídas com sucesso',
                  AlertType.success
                );
                this.selection.clear();
                this.clickPesquisar();
              }
            },
            error: err => {
              this._isLoading = false;
              this.alertService.show(
                'Erro inesperado',
                err.message || 'Erro ao excluir linhas de produto',
                AlertType.error
              );
            },
          });
        } else {
          this.linhaProdutoService.delete(item!.idLinhaProduto).subscribe({
            next: response => {
              this._isLoading = false;
              if (response.isError) {
                this.alertService.show(
                  'Atenção',
                  response.errorDescription || 'Erro ao excluir linha de produto',
                  AlertType.warning
                );
              } else {
                this.alertService.show('Sucesso', 'Linha de produto excluída com sucesso', AlertType.success);
                this.clickPesquisar();
              }
            },
            error: err => {
              this._isLoading = false;
              this.alertService.show(
                'Erro inesperado',
                err.message || 'Erro ao excluir linha de produto',
                AlertType.error
              );
            },
          });
        }
      }
    });
  }
}
